import React from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  Atom,
  Blocks,
  BookText,
  BriefcaseBusiness,
  Building2,
  ChevronRight,
  Clapperboard,
  Database,
  FileText,
  FlaskConical,
  GraduationCap,
  LayoutDashboard,
  Layers,
  PackageOpen,
  Radio,
  Settings,
  SlidersHorizontal,
  UserRound,
  Video,
  Webhook,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { HeaderActions } from '@/components/layout/HeaderActions';
import { TooltipLabel } from '@/components/ui/TooltipLabel';
import { cn } from '@/lib/utils';

export interface BreadcrumbEntry {
  label: string;
  href?: string;
  icon?: LucideIcon;
}

export interface PageHeaderProps {
  /** Page icon displayed next to the title */
  icon?: LucideIcon;
  /** Semantic color tint for the icon container */
  iconColor?: 'blue' | 'green' | 'purple' | 'orange' | 'rose' | 'indigo' | 'amber' | 'neutral';
  /** Page title */
  title?: React.ReactNode;
  /** One-line tooltip shown beside the page title */
  titleTooltip?: string;
  /** Optional subtitle / description */
  description?: string;
  /** Breadcrumb trail. If provided, renders a multi-segment breadcrumb with the
   *  last entry as the current page. If omitted, renders just the title as a
   *  single breadcrumb page. */
  breadcrumbs?: BreadcrumbEntry[];
  /** Slot for page-specific action buttons (rendered after HeaderActions) */
  actions?: React.ReactNode;
  /** Slot for metadata rendered below the title row (badges, date info, etc.) */
  metadata?: React.ReactNode;
  /** Show a bottom border (default: false) */
  border?: boolean;
}

const headerIconByLabel: Record<string, LucideIcon> = {
  Home: LayoutDashboard,
  Sessions: Video,
  People: UserRound,
  Accounts: Building2,
  'Agent Versions': Layers,
  'Agent Knowledge': GraduationCap,
  'Knowledge Gaps': GraduationCap,
  'Agent Assets': BookText,
  'Source Library': Archive,
  'Context Packs': PackageOpen,
  'Product Studio': Clapperboard,
  Simulations: FlaskConical,
  Evaluators: Atom,
  Integrations: Blocks,
  Webhooks: Webhook,
  Settings,
  Performance: Atom,
  Signal: Radio,
  Training: GraduationCap,
  'CRM Setup': BriefcaseBusiness,
  'Agent Configure': SlidersHorizontal,
  Configure: SlidersHorizontal,
  Background: Database,
};

const getTitleLabel = (title: React.ReactNode): string | null =>
  typeof title === 'string' ? title : null;

export const PageHeader: React.FC<PageHeaderProps> = ({
  icon: Icon,
  title,
  titleTooltip,
  description,
  breadcrumbs,
  actions,
  metadata,
  border = false,
}) => {
  const showStandaloneTitle = Boolean(breadcrumbs && breadcrumbs.length > 0 && title);
  const titleLabel = getTitleLabel(title);
  const ResolvedIcon = Icon ?? (titleLabel ? headerIconByLabel[titleLabel] ?? FileText : undefined);
  const titleContent = titleTooltip ? (
    <TooltipLabel
      label={title}
      description={titleTooltip}
      className="min-w-0 truncate"
    />
  ) : (
    title
  );

  return (
    <div
      className={cn(
        'flex h-11 shrink-0 items-center border-b border-border/80 px-4 sm:px-5',
        border && 'border-b-border',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2.5">
            {/* Breadcrumb / Title */}
            {breadcrumbs && breadcrumbs.length > 0 ? (
              <Breadcrumb className="min-w-0 flex-1">
                <BreadcrumbList className="min-w-0 flex-nowrap">
                  {breadcrumbs.map((crumb, idx) => {
                    const isLast = idx === breadcrumbs.length - 1;
                    const CrumbIcon = crumb.icon ?? headerIconByLabel[crumb.label];
                    return [
                      idx > 0 ? (
                        <BreadcrumbSeparator key={`${crumb.label}-separator`}>
                          <ChevronRight className="h-3.5 w-3.5" />
                        </BreadcrumbSeparator>
                      ) : null,
                      <BreadcrumbItem
                        key={crumb.label}
                        className={isLast && !showStandaloneTitle ? 'min-w-0 flex-1' : undefined}
                      >
                        {isLast ? (
                          <BreadcrumbPage className={cn(
                            showStandaloneTitle
                              ? 'text-sm font-medium text-muted-foreground'
                              : 'block min-w-0 truncate text-page-title text-foreground'
                          )}>
                            <span className="inline-flex min-w-0 items-center gap-1.5">
                              {isLast && !showStandaloneTitle && titleTooltip ? (
                                <TooltipLabel
                                  label={crumb.label}
                                  description={titleTooltip}
                                  className="min-w-0 truncate"
                                />
                              ) : (
                                <span className="min-w-0 truncate">{crumb.label}</span>
                              )}
                            </span>
                          </BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild>
                            <Link
                              to={crumb.href || '#'}
                              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {CrumbIcon && <CrumbIcon className="h-4 w-4" />}
                              {crumb.label}
                            </Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>,
                    ];
                  })}
                </BreadcrumbList>
              </Breadcrumb>
            ) : (
              <h1 className="inline-flex min-w-0 items-center gap-2 text-[14px] font-medium leading-4 text-foreground">
                {ResolvedIcon && <ResolvedIcon className="h-4 w-4 shrink-0 text-foreground" strokeWidth={1.7} />}
                {titleContent}
              </h1>
            )}
          </div>

          {showStandaloneTitle && (
            <h1 className="text-[18px] font-semibold leading-6 text-foreground">
              {titleContent}
            </h1>
          )}

          {/* Description */}
          {description && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {description}
            </p>
          )}

          {/* Metadata slot */}
          {metadata && (
            <div className="mt-1">
              {metadata}
            </div>
          )}
        </div>

        {/* Right side: actions + HeaderActions */}
        <div className="flex items-center gap-3 shrink-0">
          {actions}
          <HeaderActions />
        </div>
      </div>
    </div>
  );
};

export default PageHeader;
